<template>
  <div class="generated-products-page">
    <!-- HERO / SEARCH -->
    <header class="hero">
      <h1 class="title">Generated Products</h1>

      <div class="hero-row">
        <div class="search-wrap">
          <span class="icon" aria-hidden="true">
            <!-- Lupa inline para não depender de libs -->
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
              <path
                d="M15.5 14h-.79l-.28-.27a6.471 6.471 0 0 0 1.57-4.23C15.99 6.01 13.48 3.5 10.5 3.5S5.01 6.01 5.01 9s2.51 5.5 5.49 5.5a6.471 6.471 0 0 0 4.23-1.57l.27.28v.79l4.25 4.25c.41.41 1.08.41 1.49 0 .41-.41.41-1.08 0-1.49L15.5 14Zm-5 0C8.01 14 6 11.99 6 9.5S8.01 5 10.5 5 15 7.01 15 9.5 12.99 14 10.5 14Z"
              />
            </svg>
          </span>
          <input
            v-model.trim="q"
            type="text"
            class="search"
            placeholder="Search by name, brand, SKU, EAN or meta title…"
            @input="onSearchInput"
            @keyup.enter="applySearch"
          />
          <button class="search-btn" @click="applySearch">Search</button>
        </div>
        <div
          style="display: flex; gap: 10px; align-items: center; flex-wrap: wrap"
        >
          <span style="font-weight: 700; font-size: 12px; color: #475569"
            >Platform</span
          >

          <select
            class="input"
            style="max-width: 200px; padding: 8px 10px; border-radius: 12px"
            v-model="platform"
            @change="setPlatform(platform)"
          >
            <option
              v-for="p in platformOptions"
              :key="p.value"
              :value="p.value"
            >
              {{ p.label }}
            </option>
          </select>
        </div>

        <!-- Desktop create (o /eu cria os produtos sozinho, pela fila) -->
        <button v-if="!isEu" class="btn-create" @click="goCreate">
          <span class="plus">＋</span> Create Product
        </button>
      </div>

      <div class="actions-bar">
        <button class="action-btn priority" @click="openPrioritySkuModal">
          ⚡ Add SKU to Priority
        </button>
        <!-- //! NOVO BOTÃO: Gerir Bloqueios -->
        <button class="action-btn manage" @click="openManageBlocksModal">
          🧱 Manage Blocks
          <span v-if="lockedSummaryLoaded" class="pill-count">
            {{ lockedSkus.length }} SKUs · {{ lockedBrands.length }} Brands
          </span>
        </button>
        <!-- //! FIM NOVO BOTÃO -->

        <button v-if="!isEu" class="action-btn validate" @click="goValidate">
          ✅ Validate Products
        </button>
      </div>
    </header>

    <!-- /eu: por agora só fila e bloqueios; a lista vem na fase 2 -->
    <section v-if="isEu" class="empty">
      <h3>WooCommerce (eu)</h3>
      <p>
        Os produtos do /eu são criados automaticamente pela IA (até 5 por
        execução, nos crons da noite).<br />
        Usa <b>⚡ Add SKU to Priority</b> para pôr um SKU à frente na fila e
        <b>🧱 Manage Blocks</b> para impedir a IA de criar um SKU ou uma marca.
      </p>
      <p class="text-muted">
        A lista e a revisão dos produtos gerados para o /eu vêm a seguir.
      </p>
    </section>

    <!-- LOADING STATE -->
    <section v-else-if="loading" class="grid loading-grid">
      <div v-for="n in 8" :key="n" class="card skeleton"></div>
    </section>

    <!-- EMPTY STATE -->
    <section v-else-if="!filtered.length" class="empty">
      <img
        class="empty-ill"
        src="https://upload.wikimedia.org/wikipedia/commons/a/ac/No_image_available.svg"
        alt=""
      />
      <h3>No results</h3>
      <p>Try a different search term or clear the search.</p>
      <div class="empty-actions">
        <button class="btn-clear" @click="clearSearch">Clear search</button>
        <button class="btn-create inline" @click="goCreate">
          <span class="plus">＋</span> Create Product
        </button>
      </div>
    </section>

    <!-- CARDS GRID -->
    <section v-else class="grid">
      <article
        v-for="row in filtered"
        :key="row.id_backoffice"
        class="card"
        @dblclick="openEdit(row.id_backoffice)"
      >
        <div class="card-top">
          <img
            :src="row.img || placeholder"
            @error="onImgError"
            alt="Product image"
            class="thumb"
          />
          <div class="main">
            <div class="id">#{{ row.id_backoffice }}</div>
            <h3 class="name" :title="row.name">{{ row.name }}</h3>
            <div class="meta">
              <span class="pill"
                ><strong>Brand:</strong> {{ row.brand || '-' }}</span
              >
              <span class="pill"
                ><strong>EAN:</strong> {{ firstEAN(row.ean) }}</span
              >
              <span class="pill"
                ><strong>SKU:</strong>
                {{ Array.isArray(row.sku) ? row.sku[0] : row.sku || '-' }}</span
              >
            </div>
          </div>
        </div>

        <div class="meta-preview" v-if="row.meta_title || row.meta_description">
          <div class="meta-line">
            <span class="meta-label">Meta&nbsp;Title</span>
            <span class="meta-text" :title="row.meta_title">{{
              row.meta_title || '-'
            }}</span>
          </div>
          <div class="meta-line">
            <span class="meta-label">Meta&nbsp;Desc</span>
            <span class="meta-text two-line" :title="row.meta_description">{{
              row.meta_description || '-'
            }}</span>
          </div>
        </div>

        <div class="actions">
          <router-link
            class="btn-open"
            :to="`/product/${row.id_backoffice}`"
            target="_blank"
            title="Abrir produto no backoffice real"
          >
            Backoffice
          </router-link>
          <router-link
            class="btn-review"
            :to="{
              name: 'GeneratedProductEdit',
              params: { id: row.id_backoffice },
              query: { from: 'list', platform }, // ou 'validate'
            }"
          >
            Review
          </router-link>
        </div>
      </article>
    </section>

    <!-- Mobile FAB Create -->
    <button
      v-if="!isEu"
      class="fab-create"
      @click="goCreate"
      aria-label="Create Product"
    >
      ＋
    </button>

    <!-- //! ===== MODAL: MANAGE BLOCKS ===== -->
    <div
      v-if="showBlocksModal"
      class="blocks-backdrop"
      @click.self="closeManageBlocksModal"
    >
      <div class="blocks-modal">
        <div class="blocks-header">
          <div>
            <h3 class="blocks-title">Manage Blocks</h3>
            <div class="blocks-meta">
              <b>{{ lockedSkus.length }}</b> SKUs blocked ·
              <b>{{ lockedBrands.length }}</b> brands blocked
            </div>
          </div>

          <div class="blocks-header-actions">
            <button
              class="blocks-icon-btn"
              @click="refreshLocked"
              :disabled="blocksLoading"
            >
              🔄
            </button>
            <button
              class="blocks-icon-btn danger"
              @click="closeManageBlocksModal"
            >
              ✕
            </button>
          </div>
        </div>

        <div class="blocks-tabs">
          <button
            class="blocks-tab"
            :class="{ active: blocksTab === 'skus' }"
            @click="blocksTab = 'skus'"
          >
            SKUs
          </button>
          <button
            class="blocks-tab"
            :class="{ active: blocksTab === 'brands' }"
            @click="blocksTab = 'brands'"
          >
            Brands
          </button>
        </div>

        <div class="blocks-body">
          <!-- TAB SKUS -->
          <div v-if="blocksTab === 'skus'">
            <div class="blocks-row">
              <input
                class="blocks-search"
                v-model="skuSearch"
                placeholder="Search SKU / name / brand…"
              />

              <button
                class="blocks-btn danger"
                @click="bulkUnblockSelectedSkus"
                :disabled="blocksLoading || selectedLockedSkuRows.length === 0"
              >
                🗑️ Unblock selected ({{ selectedLockedSkuRows.length }})
              </button>
            </div>

            <div class="blocks-table-wrap">
              <table class="blocks-table">
                <thead>
                  <tr>
                    <th style="width: 36px">
                      <input
                        type="checkbox"
                        :checked="allFilteredSkusSelected"
                        @change="toggleSelectAllFilteredSkus($event)"
                      />
                    </th>
                    <th>SKU</th>
                    <th>Name</th>
                    <th>Brand</th>
                    <th style="width: 120px"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="blocksLoading">
                    <td colspan="5" class="blocks-muted">Loading…</td>
                  </tr>

                  <tr v-else-if="filteredLockedSkus.length === 0">
                    <td colspan="5" class="blocks-muted">No blocked SKUs.</td>
                  </tr>

                  <tr
                    v-else
                    v-for="row in filteredLockedSkus"
                    :key="row.id_backoffice"
                  >
                    <td>
                      <input
                        type="checkbox"
                        :value="row"
                        v-model="selectedLockedSkuRows"
                      />
                    </td>
                    <td class="blocks-mono">
                      {{ normalizeSku(row.sku) }}
                    </td>
                    <td class="blocks-truncate" :title="row.name || ''">
                      {{ row.name || '-' }}
                    </td>
                    <td class="blocks-truncate" :title="row.brand_name || ''">
                      {{ row.brand_name || '-' }}
                    </td>
                    <td style="text-align: right">
                      <button
                        class="blocks-btn danger"
                        :disabled="blocksLoading"
                        @click="unblockOneSku(normalizeSku(row.sku))"
                      >
                        Unblock
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="blocks-box">
              <div class="blocks-box-title">Block new SKUs</div>
              <textarea
                class="blocks-textarea"
                v-model="skuTextarea"
                placeholder="Paste SKUs here (one per line or comma separated)"
              ></textarea>
              <div class="blocks-box-actions">
                <button
                  class="blocks-btn primary"
                  @click="lockSkusFromTextarea"
                  :disabled="blocksLoading"
                >
                  🔒 Block SKUs
                </button>
              </div>
            </div>
          </div>

          <!-- TAB BRANDS -->
          <div v-else>
            <div class="blocks-row">
              <input
                class="blocks-search"
                v-model="brandSearch"
                placeholder="Search brand…"
              />
            </div>

            <div class="blocks-dual">
              <div class="blocks-panel">
                <div class="blocks-panel-title">
                  Available
                  <span class="blocks-pill">{{
                    filteredAvailableBrands.length
                  }}</span>
                </div>
                <select
                  class="blocks-select"
                  multiple
                  size="14"
                  v-model="selectedAvailableBrands"
                >
                  <option
                    v-for="b in filteredAvailableBrands"
                    :key="b.id"
                    :value="b"
                  >
                    {{ b.name }}
                  </option>
                </select>
                <div class="blocks-hint">Ctrl/Cmd para múltiplas.</div>
              </div>

              <div class="blocks-dual-actions">
                <button
                  class="blocks-btn primary"
                  @click="lockSelectedBrands"
                  :disabled="
                    blocksLoading || selectedAvailableBrands.length === 0
                  "
                  title="Block"
                >
                  &raquo;
                </button>
                <button
                  class="blocks-btn primary"
                  @click="unblockSelectedBrands"
                  :disabled="blocksLoading || selectedLockedBrands.length === 0"
                  title="Unblock"
                >
                  &laquo;
                </button>
              </div>

              <div class="blocks-panel">
                <div class="blocks-panel-title">
                  Blocked
                  <span class="blocks-pill">{{
                    filteredLockedBrands.length
                  }}</span>
                </div>
                <select
                  class="blocks-select"
                  multiple
                  size="14"
                  v-model="selectedLockedBrands"
                >
                  <option
                    v-for="b in filteredLockedBrands"
                    :key="b.id"
                    :value="b"
                  >
                    {{ b.name }}
                  </option>
                </select>
                <div class="blocks-hint">
                  Move para a esquerda para desbloquear.
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="blocks-footer">
          <button class="blocks-btn ghost" @click="closeManageBlocksModal">
            Close
          </button>
        </div>
      </div>
    </div>
    <!-- //! ===== END MODAL ===== -->
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import { mapGetters } from 'vuex';

// debounce simples sem lodash
const debounce = (fn, wait = 300) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), wait);
  };
};

export default {
  name: 'GeneratedProductsList',
  data() {
    return {
      loading: false,
      rows: [],
      q: '',
      appliedQ: '',
      placeholder:
        'https://upload.wikimedia.org/wikipedia/commons/a/ac/No_image_available.svg',
      API: `${this.$store.state.apiUrl}`, // termina em /
      token: localStorage.getItem('token'),

      showBlockBrandModal: false,
      brandToBlock: null,
      brandOptions: [],
      // gen_list_platform guarda também o /eu; gen_platform (lido pela validação e
      // pela edição) nunca guarda o /eu. A Shopify saiu do backoffice: um valor antigo
      // 'shopify' guardado volta ao .com
      platform:
        localStorage.getItem('gen_list_platform') === 'idivia_eu'
          ? 'idivia_eu'
          : 'woocommerce',
      platformOptions: [
        { value: 'woocommerce', label: 'WooCommerce (.com)' },
        { value: 'idivia_eu', label: 'WooCommerce (eu)' },
      ],

      // //! ===== NEW STATE for Manage Blocks =====
      showBlocksModal: false,
      blocksTab: 'skus', // 'skus' | 'brands'
      blocksLoading: false,

      lockedSkus: [],
      lockedBrands: [],
      lockedSummaryLoaded: false,

      skuSearch: '',
      skuTextarea: '',
      selectedLockedSkuRows: [],

      brandSearch: '',
      availableBrands: [],
      selectedAvailableBrands: [],
      selectedLockedBrands: [],
      // //! ===== END NEW STATE =====
    };
  },
  computed: {
    isEu() {
      return this.platform === 'idivia_eu';
    },
    filtered() {
      const s = (this.appliedQ || '').toLowerCase();
      if (!s) return this.rows;

      return this.rows.filter((r) => {
        const name = (r.name || '').toLowerCase();
        const brand = (r.brand || '').toLowerCase();
        const sku = Array.isArray(r.sku)
          ? r.sku.join(',').toLowerCase()
          : (r.sku || '').toLowerCase();
        const ean = Array.isArray(r.ean)
          ? r.ean.join(',').toLowerCase()
          : (r.ean || '').toLowerCase();
        const metaTitle = (r.meta_title || '').toLowerCase();
        const metaDesc = (r.meta_description || '').toLowerCase();

        return (
          name.includes(s) ||
          brand.includes(s) ||
          sku.includes(s) ||
          ean.includes(s) ||
          metaTitle.includes(s) ||
          metaDesc.includes(s)
        );
      });
    },
    ...mapGetters(['getBrands']),

    // //! ===== NEW computed for Manage Blocks =====
    filteredLockedSkus() {
      const q = (this.skuSearch || '').trim().toLowerCase();
      if (!q) return this.lockedSkus;

      return this.lockedSkus.filter((r) => {
        const sku = this.normalizeSku(r.sku).toLowerCase();
        const name = (r.name || '').toLowerCase();
        const brand = (r.brand_name || '').toLowerCase();
        return sku.includes(q) || name.includes(q) || brand.includes(q);
      });
    },
    allFilteredSkusSelected() {
      if (!this.filteredLockedSkus.length) return false;
      const set = new Set(
        this.selectedLockedSkuRows.map((r) => r.id_backoffice)
      );
      return this.filteredLockedSkus.every((r) => set.has(r.id_backoffice));
    },
    filteredAvailableBrands() {
      const q = (this.brandSearch || '').trim().toLowerCase();
      if (!q) return this.availableBrands;
      return this.availableBrands.filter((b) =>
        (b.name || '').toLowerCase().includes(q)
      );
    },
    filteredLockedBrands() {
      const q = (this.brandSearch || '').trim().toLowerCase();
      if (!q) return this.lockedBrands;
      return this.lockedBrands.filter((b) =>
        (b.name || '').toLowerCase().includes(q)
      );
    },
    // //! ===== END computed =====
  },
  methods: {
    setPlatform(p) {
      this.platform = p;
      localStorage.setItem('gen_list_platform', p);
      if (p !== 'idivia_eu') localStorage.setItem('gen_platform', p);
      // re-fetch para garantir que lista é da plataforma certa
      this.fetch();
      // //! refresh brands list (porque é por platform)
      this.fetchBrands();
      // //! se modal estiver aberto, atualiza também
      if (this.showBlocksModal) this.refreshLocked();
    },

    apiUrl(path) {
      // path tipo: "openai/product-description"
      const base = `${this.API}${path}`;
      const sep = base.includes('?') ? '&' : '?';
      return `${base}${sep}platform=${encodeURIComponent(this.platform)}`;
    },
    headers() {
      return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.token}`,
      };
    },
    firstEAN(eanField) {
      return Array.isArray(eanField) ? eanField[0] || '-' : eanField || '-';
    },
    async fetch() {
      // /eu: ainda não há lista (fase 2)
      if (this.isEu) {
        this.rows = [];
        return;
      }
      this.loading = true;
      try {
        // Endpoint de listagem – usa o que já tens no backend
        const { data } = await axios.get(
          this.apiUrl('openai/product-description'),
          { headers: this.headers() }
        );
        this.rows = data?.products || [];
        // guardar ids para navegação next/prev na página de edição
        localStorage.setItem(
          'gen_list_ids',
          JSON.stringify(this.rows.map((r) => r.id_backoffice))
        );
      } catch (e) {
        Swal.fire('Error', 'Failed to load generated products', 'error');
      } finally {
        this.loading = false;
      }
    },
    async fetchBrands() {
      this.loading = true;
      try {
        await this.$store.dispatch('getAllBrands');
        const brands = this.getBrands;
        console.log('Fetched brands:', brands);
        this.brandOptions = brands
          .map((b) => ({
            id: b.id,
            name: b.name,
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
      } catch (err) {
        alert(err);
      } finally {
        this.loading = false;
      }
    },
    onImgError(e) {
      e.target.src = this.placeholder;
    },
    openEdit(id) {
      // permitir abrir em nova aba com ctrl/cmd + click? (já tens o router-link)
      this.$router.push({ name: 'GeneratedProductEdit', params: { id } });
    },
    // search UX
    applySearch() {
      this.appliedQ = this.q;
    },
    clearSearch() {
      this.q = '';
      this.appliedQ = '';
    },
    onSearchInput: debounce(function () {
      // aplica pesquisa com debounce ao escrever
      this.appliedQ = this.q;
    }, 250),

    // Navega para o modo "create" na mesma página de edição (integrada)
    goCreate() {
      this.$router.push({
        name: 'GeneratedProductCreate',
        query: { platform: this.platform },
      });
    },

    goValidate() {
      this.$router.push({ name: 'GeneratedProductValidate' });
    },

    async openPrioritySkuModal() {
      const { value: skusRaw } = await Swal.fire({
        title: 'Add SKUs to priority queue',
        input: 'textarea',
        inputLabel: 'SKUs',
        inputPlaceholder:
          'ITCOS-12345\nITCOS-67890\nor: ITCOS-12345, ITCOS-67890',
        inputAttributes: {
          autocapitalize: 'off',
          autocorrect: 'off',
          spellcheck: 'false',
        },
        showCancelButton: true,
        confirmButtonText: 'Add',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#f59e0b',
        inputValidator: (value) => {
          if (!value || !value.trim()) {
            return 'At least one SKU is required';
          }

          const skus = value
            .split(/[\n,]+/)
            .map((sku) => sku.trim())
            .filter(Boolean);

          if (skus.length === 0) {
            return 'No valid SKUs detected';
          }

          if (skus.some((sku) => sku.length < 3)) {
            return 'One or more SKUs seem too short';
          }
        },
      });

      if (!skusRaw) return;

      const skus = skusRaw
        .split(/[\n,]+/)
        .map((sku) => sku.trim())
        .filter(Boolean);

      try {
        await axios.post(
          this.apiUrl('openai/add-to-priority-queue'),
          { skus },
          { headers: this.headers() }
        );

        Swal.fire({
          icon: 'success',
          title: 'Added to priority queue',
          text: `${skus.length} SKU(s) added successfully`,
          timer: 1600,
          showConfirmButton: false,
        });
      } catch (e) {
        Swal.fire(
          'Error',
          e?.response?.data?.message || 'Failed to add SKUs to priority queue',
          'error'
        );
      }
    },
    async openBlockSkuModal() {
      const { value: skusRaw } = await Swal.fire({
        title: 'Block products by SKU',
        input: 'textarea',
        inputLabel: 'SKUs',
        inputPlaceholder:
          'ITCOS-12345\nITCOS-67890\nor: ITCOS-12345, ITCOS-67890',
        inputAttributes: {
          autocapitalize: 'off',
          autocorrect: 'off',
          spellcheck: 'false',
        },
        showCancelButton: true,
        confirmButtonText: 'Block',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#dc2626',
        inputValidator: (value) => {
          if (!value || !value.trim()) {
            return 'At least one SKU is required';
          }

          const skus = value
            .split(/[\n,]+/)
            .map((sku) => sku.trim())
            .filter(Boolean);

          if (skus.length === 0) {
            return 'No valid SKUs detected';
          }

          if (skus.some((sku) => sku.length < 3)) {
            return 'One or more SKUs seem too short';
          }
        },
      });

      if (!skusRaw) return;

      const skus = skusRaw
        .split(/[\n,]+/)
        .map((sku) => sku.trim())
        .filter(Boolean);

      try {
        await axios.post(
          this.apiUrl('openai/lock-products'),
          { skus },
          { headers: this.headers() }
        );

        Swal.fire({
          icon: 'success',
          title: 'Products blocked',
          text: `${skus.length} SKU(s) blocked successfully`,
          timer: 1600,
          showConfirmButton: false,
        });
      } catch (e) {
        Swal.fire(
          'Error',
          e?.response?.data?.message || 'Failed to block SKUs',
          'error'
        );
      }
    },
    async openBlockBrandModal() {
      console.log('Available brands:', this.brandOptions);
      if (!this.brandOptions.length) {
        return Swal.fire('No brands', 'No brands available', 'info');
      }

      const optionsHtml = this.brandOptions
        .map((b) => `<option value="${b.id}">${b.name}</option>`)
        .join('');

      const { value: brand_ids } = await Swal.fire({
        title: 'Block products by brand',
        html: `
      <select id="brand-select" multiple size="12" style="
        width: 100%;
        padding: 10px;
        font-size: 14px;
      ">
        ${optionsHtml}
      </select>
      <small style="display:block;margin-top:8px;color:#64748b;">
        Tip: segura Ctrl (Windows) / Cmd (Mac) para selecionar várias.
      </small>
    `,
        width: '60rem',
        showCancelButton: true,
        confirmButtonText: 'Block',
        confirmButtonColor: '#dc2626',
        preConfirm: () => {
          const select = document.getElementById('brand-select');
          const selected = Array.from(select.selectedOptions).map(
            (o) => o.value
          );

          if (!selected.length) {
            Swal.showValidationMessage('Please select at least one brand');
            return false;
          }

          return selected; // ✅ array
        },
      });

      if (!brand_ids || !brand_ids.length) return;

      console.log('Blocking brands:', brand_ids);

      try {
        await axios.post(
          this.apiUrl('openai/lock-products'),
          { brand_ids }, // ✅ payload
          { headers: this.headers() }
        );

        const names = this.brandOptions
          .filter((b) => brand_ids.includes(String(b.id)))
          .map((b) => b.name);

        Swal.fire({
          icon: 'success',
          title: 'Brands blocked',
          text: `${brand_ids.length} brand(s) blocked: ${names
            .slice(0, 6)
            .join(', ')}${names.length > 6 ? '…' : ''}`,
          timer: 1800,
          showConfirmButton: false,
        });
      } catch (e) {
        Swal.fire(
          'Error',
          e?.response?.data?.message || 'Failed to block brand(s)',
          'error'
        );
      }
    },

    // //! ===== NEW METHODS: Manage Blocks =====
    normalizeSku(skuField) {
      if (Array.isArray(skuField)) return String(skuField[0] || '').trim();
      return String(skuField || '').trim();
    },
    parseSkusFromText(text) {
      return String(text || '')
        .split(/[\n,]+/g)
        .map((s) => s.trim())
        .filter(Boolean);
    },
    async fetchLocked() {
      const { data } = await axios.get(this.apiUrl('openai/locked-products'), {
        headers: this.headers(),
      });
      this.lockedSkus = data?.locked?.products || [];
      this.lockedBrands = data?.locked?.brands || [];
      this.lockedSummaryLoaded = true;
    },
    rebuildAvailableBrands() {
      const lockedIds = new Set(this.lockedBrands.map((b) => String(b.id)));
      this.availableBrands = (this.brandOptions || []).filter(
        (b) => !lockedIds.has(String(b.id))
      );
    },
    async openManageBlocksModal() {
      this.showBlocksModal = true;
      this.blocksTab = 'skus';
      this.blocksLoading = true;
      try {
        await this.fetchLocked();
        this.rebuildAvailableBrands();
        this.selectedAvailableBrands = [];
        this.selectedLockedBrands = [];
        this.selectedLockedSkuRows = [];
        this.skuSearch = '';
        this.brandSearch = '';
      } catch (e) {
        Swal.fire(
          'Error',
          e?.response?.data?.message || 'Failed to load blocked items',
          'error'
        );
      } finally {
        this.blocksLoading = false;
      }
    },
    closeManageBlocksModal() {
      this.showBlocksModal = false;
    },
    async refreshLocked() {
      this.blocksLoading = true;
      try {
        await this.fetchLocked();
        this.rebuildAvailableBrands();
        this.selectedAvailableBrands = [];
        this.selectedLockedBrands = [];
        this.selectedLockedSkuRows = [];
      } finally {
        this.blocksLoading = false;
      }
    },
    toggleSelectAllFilteredSkus(ev) {
      const checked = ev.target.checked;
      if (!checked) {
        const filteredIds = new Set(
          this.filteredLockedSkus.map((r) => r.id_backoffice)
        );
        this.selectedLockedSkuRows = this.selectedLockedSkuRows.filter(
          (r) => !filteredIds.has(r.id_backoffice)
        );
        return;
      }
      const byId = new Map(
        this.selectedLockedSkuRows.map((r) => [r.id_backoffice, r])
      );
      for (const r of this.filteredLockedSkus) byId.set(r.id_backoffice, r);
      this.selectedLockedSkuRows = Array.from(byId.values());
    },
    async unblockOneSku(sku) {
      this.blocksLoading = true;
      try {
        await axios.post(
          this.apiUrl('openai/unlock-products'),
          { skus: [sku] },
          { headers: this.headers() }
        );
        await this.fetchLocked();
        this.rebuildAvailableBrands();
        this.selectedLockedSkuRows = this.selectedLockedSkuRows.filter(
          (r) => this.normalizeSku(r.sku) !== sku
        );
      } catch (e) {
        Swal.fire(
          'Error',
          e?.response?.data?.message || 'Failed to unblock SKU',
          'error'
        );
      } finally {
        this.blocksLoading = false;
      }
    },
    async bulkUnblockSelectedSkus() {
      const skus = this.selectedLockedSkuRows
        .map((r) => this.normalizeSku(r.sku))
        .filter(Boolean);

      if (!skus.length) return;

      const res = await Swal.fire({
        icon: 'warning',
        title: 'Confirm',
        text: `Unblock ${skus.length} SKU(s)?`,
        showCancelButton: true,
        confirmButtonText: 'Yes, unblock',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#dc2626',
      });

      if (!res.isConfirmed) return;

      this.blocksLoading = true;
      try {
        await axios.post(
          this.apiUrl('openai/unlock-products'),
          { skus },
          { headers: this.headers() }
        );
        await this.fetchLocked();
        this.rebuildAvailableBrands();
        this.selectedLockedSkuRows = [];
      } catch (e) {
        Swal.fire(
          'Error',
          e?.response?.data?.message || 'Failed to unblock SKUs',
          'error'
        );
      } finally {
        this.blocksLoading = false;
      }
    },
    async lockSkusFromTextarea() {
      const skus = this.parseSkusFromText(this.skuTextarea);
      if (!skus.length) {
        Swal.fire('Info', 'Please insert at least one SKU.', 'info');
        return;
      }

      this.blocksLoading = true;
      try {
        await axios.post(
          this.apiUrl('openai/lock-products'),
          { skus },
          { headers: this.headers() }
        );
        this.skuTextarea = '';
        await this.fetchLocked();
        this.rebuildAvailableBrands();
      } catch (e) {
        Swal.fire(
          'Error',
          e?.response?.data?.message || 'Failed to block SKUs',
          'error'
        );
      } finally {
        this.blocksLoading = false;
      }
    },
    async lockSelectedBrands() {
      const brand_ids = this.selectedAvailableBrands.map((b) => b.id);
      if (!brand_ids.length) return;

      this.blocksLoading = true;
      try {
        await axios.post(
          this.apiUrl('openai/lock-products'),
          { brand_ids },
          { headers: this.headers() }
        );
        await this.fetchLocked();
        this.rebuildAvailableBrands();
        this.selectedAvailableBrands = [];
      } catch (e) {
        Swal.fire(
          'Error',
          e?.response?.data?.message || 'Failed to block brands',
          'error'
        );
      } finally {
        this.blocksLoading = false;
      }
    },
    async unblockSelectedBrands() {
      const brand_ids = this.selectedLockedBrands.map((b) => b.id);
      if (!brand_ids.length) return;

      this.blocksLoading = true;
      try {
        await axios.post(
          this.apiUrl('openai/unlock-products'),
          { brand_ids },
          { headers: this.headers() }
        );
        await this.fetchLocked();
        this.rebuildAvailableBrands();
        this.selectedLockedBrands = [];
      } catch (e) {
        Swal.fire(
          'Error',
          e?.response?.data?.message || 'Failed to unblock brands',
          'error'
        );
      } finally {
        this.blocksLoading = false;
      }
    },
    // //! ===== END NEW METHODS =====
  },

  async created() {
    await this.fetch();
    await this.fetchBrands();
  },
};
</script>

<style scoped>
/* ====== Layout base ====== */
.generated-products-page {
  max-width: 1280px;
  margin: 0 auto;
  padding: 24px;
  font-family: 'Inter', system-ui, -apple-system, Segoe UI, Roboto,
    'Helvetica Neue', Arial, sans-serif;
  color: #1f2937; /* slate-800 */
}

/* ====== Hero + Search ====== */
.hero {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 18px;
}

.title {
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.02em;
  margin: 0;
  color: #0f172a; /* slate-900 */
}

.hero-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  align-items: center;
}

.search-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  background: #fff;
  border: 1px solid #e5e7eb; /* gray-200 */
  border-radius: 999px;
  padding: 10px 12px 10px 14px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.search-wrap:focus-within {
  border-color: #cbd5e1; /* gray-300 */
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.06);
}

.icon {
  display: inline-flex;
  color: #64748b; /* slate-500 */
}

.search {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 15px;
  padding: 6px 4px;
  color: #0f172a;
}

.search::placeholder {
  color: #94a3b8;
}

.search-btn {
  border: none;
  background: #111827; /* gray-900 */
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  padding: 8px 14px;
  border-radius: 999px;
  cursor: pointer;
  transition: transform 0.06s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.search-btn:hover {
  background: #0b1220;
  box-shadow: 0 4px 14px rgba(17, 24, 39, 0.18);
}

.search-btn:active {
  transform: translateY(1px);
}

/* Create button (desktop) */
.btn-create {
  border: 1px solid #0f172a;
  background: #0f172a;
  color: #fff;
  padding: 10px 14px;
  border-radius: 12px;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: transform 0.06s ease, box-shadow 0.2s ease, background 0.2s ease,
    border-color 0.2s ease;
}

.btn-create .plus {
  font-size: 16px;
  line-height: 1;
}

.btn-create:hover {
  background: #0b1220;
  border-color: #0b1220;
  box-shadow: 0 8px 18px rgba(2, 8, 23, 0.15);
}

.btn-create:active {
  transform: translateY(1px);
}

/* ===== Actions bar ===== */
.actions-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 4px;
}

/* Base button */
.action-btn {
  border-radius: 12px;
  padding: 10px 14px;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: transform 0.06s ease, box-shadow 0.2s ease, background 0.2s ease;
}

/* Variants */
.action-btn.priority {
  border-color: #f59e0b;
  background: #fff7ed;
  color: #b45309;
}

.action-btn.validate {
  border-color: #10b981;
  background: #ecfdf5;
  color: #047857;
}

.action-btn.block {
  border-color: #dc2626;
  background: #fef2f2;
  color: #991b1b;
}

/* //! NEW variant */
.action-btn.manage {
  border-color: #6366f1;
  background: #eef2ff;
  color: #3730a3;
}
/* //! badge */
.pill-count {
  margin-left: 8px;
  font-size: 12px;
  padding: 3px 8px;
  border-radius: 999px;
  border: 1px solid rgba(99, 102, 241, 0.25);
  background: rgba(255, 255, 255, 0.7);
}
/* //! END NEW variant */

/* Hover */
.action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(2, 8, 23, 0.12);
}

/* Active */
.action-btn:active {
  transform: translateY(0);
}

/* ====== Grid ====== */
.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 14px;
}

/* ====== Card minimal (C3) ====== */
.card {
  background: #fff;
  border: 1px solid #eef2f7; /* ultra-light */
  border-radius: 16px;
  padding: 14px;
  transition: transform 0.15s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  will-change: transform;
}

.card:hover {
  transform: translateY(-2px);
  border-color: #e5e7eb;
  box-shadow: 0 10px 30px rgba(2, 8, 23, 0.06);
}

/* Top block */
.card-top {
  display: grid;
  grid-template-columns: 72px 1fr;
  gap: 12px;
  align-items: center;
}

.thumb {
  width: 72px;
  height: 72px;
  object-fit: contain;
  border-radius: 12px;
  background: #f8fafc; /* slate-50 */
  border: 1px solid #eef2f7;
}

.main {
  min-width: 0;
}

.id {
  font-size: 11px;
  font-weight: 700;
  color: #475569;
  background: #f1f5f9; /* slate-100 */
  padding: 2px 8px;
  border-radius: 999px;
  width: fit-content;
  margin-bottom: 6px;
  border: 1px solid #e2e8f0;
}

.name {
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 6px 0;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.meta {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.pill {
  font-size: 12px;
  color: #1f2937;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  padding: 4px 8px;
}

/* Meta preview block */
.meta-preview {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #e5e7eb;
}

.meta-line {
  display: grid;
  grid-template-columns: 92px 1fr;
  gap: 10px;
  align-items: baseline;
  margin-bottom: 6px;
}

.meta-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
}

.meta-text {
  font-size: 13px;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.two-line {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  white-space: normal;
}

/* Actions */
.actions {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
}

.btn-review {
  border: 1px solid #0f172a;
  background: #0f172a;
  color: #fff;
  padding: 8px 12px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: transform 0.06s ease, box-shadow 0.2s ease, background 0.2s ease,
    border-color 0.2s ease;
}

.btn-review:hover {
  background: #0b1220;
  border-color: #0b1220;
  box-shadow: 0 8px 18px rgba(2, 8, 23, 0.15);
}

.btn-review:active {
  transform: translateY(1px);
}

/* ====== Loading / Empty ====== */
.loading-grid .card.skeleton {
  height: 150px;
  border-radius: 16px;
  background: linear-gradient(90deg, #f6f7f9 25%, #eef1f5 37%, #f6f7f9 63%);
  background-size: 400% 100%;
  animation: shimmer 1.2s ease-in-out infinite;
  border: none;
}

@keyframes shimmer {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: 0 0;
  }
}

.empty {
  padding: 40px 0 60px;
  text-align: center;
  color: #334155;
}

.empty-ill {
  width: 76px;
  height: 76px;
  object-fit: contain;
  opacity: 0.55;
  margin-bottom: 10px;
}

.empty .empty-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
}

.btn-clear {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #111827;
  border-radius: 10px;
  padding: 8px 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;
}
.btn-clear:hover {
  background: #eaf0f7;
  border-color: #d7dde6;
}

/* ===== FAB (mobile) ===== */
.fab-create {
  position: fixed;
  right: 18px;
  bottom: 18px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  background: #0f172a;
  color: #fff;
  font-weight: 800;
  font-size: 24px;
  box-shadow: 0 14px 28px rgba(2, 8, 23, 0.22);
  cursor: pointer;
  display: none; /* desktop hidden */
  align-items: center;
  justify-content: center;
  transition: transform 0.06s ease, box-shadow 0.2s ease, background 0.2s ease;
  z-index: 20;
}

.fab-create:hover {
  background: #0b1220;
  box-shadow: 0 18px 40px rgba(2, 8, 23, 0.28);
}

.fab-create:active {
  transform: translateY(1px);
}

.btn-open {
  border: 1px solid #0f172a;
  background: #ffffff;
  color: #0f172a;
  padding: 8px 12px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: transform 0.06s ease, box-shadow 0.2s ease, background 0.2s ease,
    border-color 0.2s ease;
  margin-left: 8px;
}

.btn-open:hover {
  background: #f8fafc;
  border-color: #0b1220;
  box-shadow: 0 8px 18px rgba(2, 8, 23, 0.1);
}

.btn-open:active {
  transform: translateY(1px);
}

.btn-priority {
  border: 1px solid #f59e0b;
  background: #fff7ed;
  color: #b45309;
  padding: 10px 14px;
  border-radius: 12px;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  transition: transform 0.06s ease, box-shadow 0.2s ease;
}

.btn-priority:hover {
  background: #fffbeb;
  box-shadow: 0 8px 18px rgba(245, 158, 11, 0.25);
}

/* dropdown do vue-select */
.vs__dropdown-menu {
  z-index: 100000 !important;
}

/* Show FAB on small screens and hide desktop create */
@media (max-width: 768px) {
  .btn-create {
    display: none;
  }
  .fab-create {
    display: inline-flex;
  }
  .hero-row {
    grid-template-columns: 1fr;
  }
}

/* //! ===== NEW STYLES: Manage Blocks Modal ===== */
.blocks-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(2, 8, 23, 0.55);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
}

.blocks-modal {
  width: min(1100px, 96vw);
  max-height: 92vh;
  background: #fff;
  border-radius: 18px;
  border: 1px solid #e5e7eb;
  box-shadow: rgba(0, 0, 0, 0.25) 0px 20px 60px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.blocks-header {
  padding: 14px 16px;
  border-bottom: 1px solid #eef2f7;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}

.blocks-title {
  margin: 0;
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
}

.blocks-meta {
  margin-top: 2px;
  font-size: 12px;
  color: #64748b;
}

.blocks-header-actions {
  display: flex;
  gap: 10px;
}

.blocks-icon-btn {
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 12px;
  padding: 8px 10px;
  cursor: pointer;
  font-weight: 800;
}

.blocks-icon-btn.danger {
  border-color: #fecaca;
  background: #fff;
}

.blocks-tabs {
  padding: 10px 16px;
  border-bottom: 1px solid #eef2f7;
  display: inline-flex;
  gap: 8px;
  background: #f8fafc;
}

.blocks-tab {
  border: 1px solid transparent;
  background: transparent;
  padding: 8px 12px;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 900;
  color: #475569;
}

.blocks-tab.active {
  background: #fff;
  border-color: #e5e7eb;
  color: #0f172a;
  box-shadow: rgba(0, 0, 0, 0.06) 0px 6px 16px;
}

.blocks-body {
  padding: 16px;
  overflow: auto;
}

.blocks-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.blocks-search {
  width: 360px;
  max-width: 70vw;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  outline: none;
}

.blocks-btn {
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 12px;
  padding: 9px 12px;
  font-weight: 900;
  cursor: pointer;
}

.blocks-btn.primary {
  border-color: #c7d2fe;
  background: #eef2ff;
  color: #3730a3;
}

.blocks-btn.danger {
  border-color: #fecaca;
  background: #fef2f2;
  color: #991b1b;
}

.blocks-btn.ghost {
  background: #fff;
}

.blocks-table-wrap {
  border: 1px solid #eef2f7;
  border-radius: 14px;
  overflow: auto;
  background: #fff;
}

.blocks-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.blocks-table thead th {
  text-align: left;
  background: #f8fafc;
  border-bottom: 1px solid #eef2f7;
  padding: 10px;
  color: #475569;
}

.blocks-table tbody td {
  border-bottom: 1px solid #f1f5f9;
  padding: 10px;
}

.blocks-muted {
  color: #64748b;
  text-align: center;
  padding: 18px 10px;
}

.blocks-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    'Liberation Mono', 'Courier New', monospace;
}

.blocks-truncate {
  max-width: 420px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.blocks-box {
  margin-top: 12px;
  border: 1px solid #eef2f7;
  border-radius: 16px;
  padding: 12px;
  background: #fbfdff;
}

.blocks-box-title {
  font-weight: 900;
  color: #0f172a;
  margin-bottom: 10px;
}

.blocks-textarea {
  width: 100%;
  min-height: 110px;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 12px;
  outline: none;
  font-size: 13px;
}

.blocks-box-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
}

.blocks-dual {
  display: grid;
  grid-template-columns: 1fr 110px 1fr;
  gap: 12px;
  align-items: center;
}

.blocks-panel {
  border: 1px solid #eef2f7;
  border-radius: 16px;
  padding: 12px;
  background: #fff;
}

.blocks-panel-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 900;
  margin-bottom: 10px;
}

.blocks-pill {
  font-size: 12px;
  padding: 3px 8px;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #f8fafc;
  color: #475569;
}

.blocks-select {
  width: 100%;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 10px;
  outline: none;
}

.blocks-dual-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  justify-content: center;
}

.blocks-hint {
  margin-top: 8px;
  font-size: 12px;
  color: #64748b;
}

.blocks-footer {
  padding: 12px 16px;
  border-top: 1px solid #eef2f7;
  display: flex;
  justify-content: flex-end;
}
/* //! ===== END NEW STYLES ===== */
</style>
